import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Outlet, useMatch, useParams } from 'react-router-dom';
import { makeStyles, tokens, Spinner } from '@fluentui/react-components';
import { Folder20Filled } from '@fluentui/react-icons';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { ResourceErrorPage } from '@/components/shared/ResourceErrorPage';
import { ApiError } from '@/services/apiError';
import { projectNavSections } from '@/components/Sidebar/projectSidebarConfig';
import { Sidebar, type SidebarSection } from '@/components/Sidebar/Sidebar';
import { useTelemetry } from '@/hooks/useTelemetry';
import { useAppStore } from '@/stores/appStore';
import { useRecentWorkStore } from '@/stores/recentWorkStore';
import { paths } from '@/routes';
import { tenantPrefix } from '@/routes';

// Sections are mostly static. Two pieces need runtime context:
//   1. The Home item's path must be tenant-aware so clicking Home from
//      inside a tenant doesn't drop the user back to the bare root.
//   2. The project section heading shows the active project's name plus a
//      folder icon — both resolved per-render via useMemo below.
const STATIC_SIDEBAR_SECTIONS: SidebarSection[] = projectNavSections.map((s) => ({
  id: s.id,
  label: s.label,
  ...(s.icon ? { icon: s.icon } : {}),
  items: s.items.map((it) => ({
    id: it.id,
    label: it.label,
    icon: it.icon,
    path: it.path,
    end: it.end,
  })),
}));

const useStyles = makeStyles({
  body: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
    minHeight: 0,
  },
  mainContent: {
    flex: 1,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    // Asymmetric top padding pulls page-level headings up so they sit
    // alongside the sidebar Home button instead of leaving a large gap
    // under the global header. Mirrors `AppLayout.content` so project- and
    // app-scoped pages share the same outer chrome. Nested app routes
    // (`apps/:appName/*`) take over via AppLayout and apply their own
    // padding, so the values won't compound.
    paddingTop: tokens.spacingVerticalM,
    paddingRight: tokens.spacingHorizontalXXXL,
    paddingBottom: tokens.spacingVerticalXXXL,
    paddingLeft: tokens.spacingHorizontalXXXL,
    background: `radial-gradient(ellipse 80% 60% at 70% 20%, rgba(91,108,240,0.04), transparent), radial-gradient(ellipse 60% 40% at 20% 80%, rgba(52,211,153,0.03), transparent)`,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    padding: tokens.spacingVerticalXXXL,
  },
});

export const ProjectLayout = () => {
  const styles = useStyles();
  const { trackEvent } = useTelemetry();
  const { tenantId: routeTenantId } = useParams<{ tenantId?: string }>();
  const { projectName, project, locationName, isLoading, notFound, error } = useCurrentProject();
  const locationInStore = useAppStore((state) => state.selectedLocation);
  const selectedTenantId = useAppStore((state) => state.selectedTenantId);
  const prevProjectRef = useRef('');

  // Inject runtime context into the static section template:
  //  - Home item path becomes tenant-aware so it doesn't escape the tenant.
  //  - Project section heading shows the current project's display name and
  //    a folder icon, with `indentItems` so the features visually nest under
  //    it. We fall back to the route segment (and finally "Project") while
  //    the project is still loading so the heading never disappears.
  const sections = useMemo<SidebarSection[]>(() => {
    const homeHref = tenantPrefix(selectedTenantId) || '/';
    const projectDisplayName = project?.name || projectName || 'Project';
    return STATIC_SIDEBAR_SECTIONS.map((section) => {
      if (section.id === 'home') {
        return {
          ...section,
          items: section.items.map((it) => (it.id === 'home' ? { ...it, path: homeHref } : it)),
        };
      }
      if (section.id === 'project') {
        return {
          ...section,
          categoryLabel: 'Project',
          label: projectDisplayName,
          icon: Folder20Filled,
          brandIcon: true,
          indentItems: true,
        };
      }
      return section;
    });
  }, [selectedTenantId, project, projectName]);

  const handleSidebarItemClick = useCallback(
    (id: string) => trackEvent('navigation.sidebar.clicked', { destination: id }),
    [trackEvent],
  );

  // When inside an app, AppLayout owns the sidebar — ProjectLayout just passes through.
  const matchBare = useMatch('/location/:locationName/projects/:projectName/apps/:appName/*');
  const matchTenant = useMatch('/tenant/:tenantId/location/:locationName/projects/:projectName/apps/:appName/*');
  const isInsideApp = !!(matchBare || matchTenant);

  // Bridge: sync URL-derived project context to the store ONLY after the project
  // is confirmed to exist. Avoids persisting invalid project names from bad URLs,
  // which would cause redirect loops via LastSessionRedirect.
  // Also seeds `selectedLocation` from the URL — without this, deep-link entry
  // into a project route leaves the operation-picker prefetch scope unresolved
  // (see useScopeKey in useAllOperations).
  useEffect(() => {
    if (!projectName || isLoading || notFound || !project) return;
    const projectKey = `${routeTenantId ?? ''}|${locationName ?? ''}|${projectName}`;
    if (projectKey !== prevProjectRef.current) {
      prevProjectRef.current = projectKey;
      useAppStore.getState().hydrateRouteContext({ projectName, ...(locationName ? { locationName } : {}) });
      const entryTenantId = routeTenantId ?? useAppStore.getState().selectedTenantId;
      // Prefer the tenant URL param when present and fall back to the store
      // for non-tenant routes or before store hydration catches up. Using
      // only the store caused recordAccess to no-op on earlier navigation.
      const recordLocation = locationName ?? locationInStore;
      if (entryTenantId && recordLocation) {
        useRecentWorkStore.getState().recordAccess({
          type: 'project',
          displayName: projectName,
          location: recordLocation,
          project: projectName,
          app: '',
          href: paths.projectApps(recordLocation, projectName, routeTenantId),
          tenantId: entryTenantId,
        });
      }
    }
  }, [projectName, locationName, locationInStore, project, isLoading, notFound, routeTenantId]);

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="medium" label="Loading project…" />
      </div>
    );
  }

  if (notFound) {
    return <ResourceErrorPage resourceType="project" error={new ApiError('Not found', 404)} />;
  }

  if (error) {
    return <ResourceErrorPage resourceType="project" error={error} />;
  }

  // Inside an app — no project sidebar, AppLayout handles its own sidebar
  if (isInsideApp) {
    return <Outlet />;
  }

  return (
    <div className={styles.body}>
      <Sidebar
        ariaLabel="Project navigation"
        navAriaLabel="Project"
        sections={sections}
        onItemClick={handleSidebarItemClick}
      />
      <div className={styles.mainContent}>
        <Outlet />
      </div>
    </div>
  );
};
